"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _OrderQuestion = _interopRequireDefault(require("./OrderQuestion.js"));
var _OrderQuestionSection = _interopRequireDefault(require("./OrderQuestionSection.js"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _material = require("@heron-web/material");
var _hopsSdkJs = require("@hps/hops-sdk-js");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/**
 * Renders an order's question set
 * 
 * @package HOPS
 * @subpackage OrderQuestions
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class OrderQuestionSet extends _react.default.PureComponent {
  /**
   * `formRef`
   * 
   * @type {ReactRef}
   */
  formRef = /*#__PURE__*/_react.default.createRef();

  /**
   * Question value changed.
   *
   * @param {mixed} value
   * @param {String} questionIdentifier
   * @return {void}
   */
  handleChange = (value, questionIdentifier) => {
    const answers = {
      ...this.props.answers
    };
    const [itemClaimUuid, questionId, unitIndex, personIndex] = questionIdentifier.split(".");
    if (itemClaimUuid === "order") {
      answers.Order = {
        ...answers.Order,
        [questionId]: value
      };
    } else if (personIndex !== undefined) {
      answers.People = {
        ...answers.People,
        [personIndex]: {
          ...answers.People?.[personIndex],
          [itemClaimUuid]: {
            ...answers.People?.[personIndex]?.[itemClaimUuid],
            [unitIndex]: {
              ...answers.People?.[personIndex]?.[itemClaimUuid]?.[unitIndex],
              [questionId]: value
            }
          }
        }
      };
    } else {
      answers.Items = {
        ...answers.Items,
        [itemClaimUuid]: {
          ...answers.Items?.[itemClaimUuid],
          [unitIndex]: {
            ...answers.Items?.[itemClaimUuid]?.[unitIndex],
            [questionId]: value
          }
        }
      };
    }
    this.props.onChange(answers);
  };

  /**
   * Check validity.
   *
   * @return {Boolean}
   */
  checkValidity = () => this.formRef?.current?.checkValidity?.();

  /**
   * Report validity.
   * 
   * @return {void}
   */
  reportValidity = () => this.formRef?.current?.reportValidity?.();

  /**
   * Render.
   * 
   * @return {ReactNode}
   */
  render() {
    const questionNodes = [];

    /**
     * Iterate our item claims
     */
    for (const itemClaim of this.props.itemClaims) {
      /**
       * Get the number of people this item relates to
       */
      const peopleCount = _hopsSdkJs.CheckoutBasketItem.getTargetPeopleCount(itemClaim);

      /**
       * Questions to ask once per-unit of the item
       */
      const perItemQuestions = this.mapQuestionInstanceQuestions(this.props.questions.Items[itemClaim.Uuid]) || [];

      /**
       * Questions to ask once per-person per-unit of the item
       */
      const perPersonQuestions = this.mapQuestionInstanceQuestions(this.props.questions.People[itemClaim.Uuid]) || [];

      /**
       * Render the per-item questions (we want them once only)
       */
      if (perItemQuestions?.length) {
        questionNodes.push(this.renderItemClaimQuestions(itemClaim, perItemQuestions));
      }

      /**
       * Render the per-person questions
       */
      if (perPersonQuestions?.length) {
        questionNodes.push(this.renderItemClaimQuestions(itemClaim, perPersonQuestions, peopleCount, true, /** Hide the "Questions About XXX" label when there's only one person, and we've already rendered some per-item questions above, because otherwise it would appear to be repeated */
        !(peopleCount === 1 && perItemQuestions?.length)));
      }
    }

    /**
     * Render!
     */
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("form", {
      ref: this.formRef,
      onSubmit: this.constructor.handleSubmitSwallow,
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_material.Flex, {
        gap: 2,
        children: [questionNodes, this.renderOrderQuestions(), !questionNodes?.length && !this.perOrderQuestions?.length && this.renderEmpty()]
      })
    });
  }

  /**
   * Render the empty state.
   * 
   * @return {ReactNode}
   */
  renderEmpty() {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_material.String, {
      str: this.props.noQuestionsText || "No questions to display."
    });
  }

  /**
   * Render questions relating to a given item claim.
   * 
   * @param {Object} itemClaim
   * @param {Array} questions
   * @param {Integer} peopleCount
   * @param {Boolean} usePersonIndex optional Map as a per-person question (`false`)
   * @param {Boolean} showLabel optional Show the section label (`true`)
   * @return {ReactNode}
   */
  renderItemClaimQuestions(itemClaim, questions) {
    let peopleCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
    let usePersonIndex = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    let showLabel = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
    const nodes = [];
    const quantity = itemClaim.Quantity;

    /**
     * Iterate each unit of the item...
     */
    for (let i = 0; i < quantity; i++) {
      /**
       * ...and each person...
       */
      for (let p = 0; p < peopleCount; p++) {
        /**
         * Text identifying the item this question section is for
         */
        const unitText = [quantity > 1 ? `Unit ${i + 1}` : null, peopleCount > 1 ? `Person ${p + 1}` : null].filter(s => s).join(", ");

        /**
         * Label text
         */
        const label = _hopsSdkJs.CheckoutBasketItem.getUiLabel(itemClaim);
        const subtext = _hopsSdkJs.CheckoutBasketItem.getUiSubtext(itemClaim);

        /**
         * Render the questions in this section
         */
        nodes.push( /*#__PURE__*/(0, _jsxRuntime.jsx)(_OrderQuestionSection.default, {
          mt: !showLabel ? -1 : undefined,
          label: showLabel ? `Questions About ${label}${subtext ? ` (${subtext})` : ""}${unitText ? ` (${unitText})` : ""}` : undefined,
          children: questions.map(question => {
            return this.renderQuestion(question.Question, itemClaim.Uuid, i, usePersonIndex ? p : null, question.Mandatory);
          })
        }, `item_${itemClaim.Uuid}_${i}_${p}`));
      }
    }
    return nodes;
  }

  /**
   * Render our per-order questions.
   *
   * @return {ReactNode}
   */
  renderOrderQuestions() {
    const questions = this.perOrderQuestions;
    if (!questions.length) return null;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_OrderQuestionSection.default, {
      label: this.props.perOrderQuestionsSectionLabel || "Questions About Your Order",
      children: questions.map(question => {
        return this.renderQuestion(question.Question, null, null, null, question.Mandatory);
      })
    }, "order");
  }

  /**
   * Render a question object.
   *
   * @param {Object} question
   * @param {String|null} itemClaimUuid UUID of item claim (`null` = order question)
   * @param {Integer|null} unitIndex Index (unit count) of this question
   * @param {Integer|null} personIndex Index (person count) of this question
   * @param {Boolean} required
   * @return {ReactNode}
   */
  renderQuestion(question, itemClaimUuid, unitIndex, personIndex, required) {
    let answerValue;
    if (!itemClaimUuid) answerValue = this.props.answers?.Order?.[question.Id];else if (personIndex === null) answerValue = this.props.answers?.Items?.[itemClaimUuid]?.[unitIndex]?.[question.Id];else answerValue = this.props.answers?.People?.[personIndex]?.[itemClaimUuid]?.[unitIndex]?.[question.Id];
    let id = `${itemClaimUuid || "order"}.${question.Id}`;
    if (unitIndex !== null) id += `.${unitIndex}`;
    if (personIndex !== null) id += `.${personIndex}`;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_OrderQuestion.default, {
      answerValue: answerValue,
      color: this.props.color,
      hasOptionalNoAnswerAcknowledgement: this.props.optionalNoAnswerAcknowledgements?.[id] || false,
      id: id,
      onChange: this.handleChange,
      onChangeOptionalNoAnswerAcknowledgement: this.props.onChangeOptionalNoAnswerAcknowledgement,
      question: question,
      required: required,
      requireOptionalNoAnswerAcknowledgement: this.props.requireOptionalNoAnswerAcknowledgement
    }, id);
  }

  /**
   * Find a question definition object by its ID.
   *
   * @param {Integer} id
   * @return {Object|null}
   */
  findQuestion(id) {
    return this.props.questions.Questions.find(q => q.Id === id);
  }

  /**
   * Map question instance objects to have a hydrated question object 
   * as their `Question` property, instead of the ID of the question.
   *
   * @param {Array<Object>} Question instance objects to map
   * @return {Array<Object>}
   */
  mapQuestionInstanceQuestions(instances) {
    return instances?.map(q => ({
      ...q,
      Question: this.findQuestion(q.Question)
    }));
  }

  /**
   * Get the per-order questions to ask where one response is needed.
   *
   * @return {Array<Object>}
   */
  get perOrderQuestions() {
    return this.mapQuestionInstanceQuestions(this.props.questions.Order).filter((v, i, a) => a.indexOf(a.find(q => q.Question.Id === v.Question.Id)) === i).map(q => ({
      ...q,
      Mandatory: !!this.props.questions.Order.find(qi => qi.Question === q.Question.Id && qi.Mandatory)
    }));
  }

  /**
   * Swallow form submit events.
   * 
   * @param {Event} e
   * @return {void}
   */
  static handleSubmitSwallow(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  static propTypes = {
    color: _propTypes.default.oneOf(["primary", "secondary"]),
    answers: _propTypes.default.object,
    itemClaims: _propTypes.default.array,
    noQuestionsText: _propTypes.default.string,
    onChange: _propTypes.default.func,
    onChangeOptionalNoAnswerAcknowledgement: _propTypes.default.func,
    optionalNoAnswerAcknowlegements: _propTypes.default.object,
    perOrderQuestionsSectionLabel: _propTypes.default.string,
    requireOptionalNoAnswerAcknowledgement: _propTypes.default.bool,
    questions: _propTypes.default.object
  };
  static defaultProps = {
    color: "primary",
    answers: {},
    itemClaims: [],
    optionalNoAnswerAcknowlegements: null,
    requireOptionalNoAnswerAcknowledgement: false,
    questions: {}
  };
}
var _default = OrderQuestionSet;
exports.default = _default;